// MIT License - Copyright (c) 2026 Doug McNall

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './Tooltip.css';

/**
 * Tooltip Component
 * 
 * Reusable tooltip wrapper for UI elements
 */
const Tooltip = ({ children, text, position = 'top', delay = 500 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const wrapperRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => {
      if (wrapperRef.current) {
        const rect = wrapperRef.current.getBoundingClientRect();
        setTooltipPosition({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height
        });
        setIsVisible(true);
      }
    }, delay);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  const getTooltipStyle = () => {
    const { top, left, width, height } = tooltipPosition;
    
    switch (position) {
      case 'top':
        return {
          top: `${top - 8}px`,
          left: `${left + width / 2}px`,
          transform: 'translate(-50%, -100%)'
        };
      case 'bottom':
        return {
          top: `${top + height + 8}px`,
          left: `${left + width / 2}px`,
          transform: 'translateX(-50%)'
        };
      case 'left':
        return {
          top: `${top + height / 2}px`,
          left: `${left - 8}px`,
          transform: 'translate(-100%, -50%)'
        };
      case 'right':
        return {
          top: `${top + height / 2}px`,
          left: `${left + width + 8}px`,
          transform: 'translateY(-50%)'
        };
      default:
        return {};
    }
  };

  return (
    <>
      <span
        ref={wrapperRef}
        className="tooltip-wrapper"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </span>
      {isVisible && createPortal(
        <div className={`tooltip tooltip-${position}`} style={getTooltipStyle()}>
          <div className="tooltip-content">{text}</div>
          <div className="tooltip-arrow" />
        </div>,
        document.body
      )}
    </>
  );
};

export default Tooltip;
